/**
 * NewSectionsEditor.js
 * Admin editors for all new homepage sections.
 * Features: section ordering (up/down), enable/disable toggle,
 *           image upload, category/product selection, full CRUD per section.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Plus, Trash2, ChevronDown, ChevronUp, GripVertical,
  Zap, Star, ShoppingBag, Users, Heart, Grid, Layers,
  Gift, Smartphone, Film, ArrowUp, ArrowDown, Upload, X,
} from "lucide-react";
import ImageUploadInput from "../../components/ImageUploadInput";
import MediaUploadInput from "../../components/MediaUploadInput";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Section metadata ──────────────────────────────────────────────────────
const DEFAULT_ORDER = [
  "category_icons_row", "secondary_slider", "shop_by_price", "flash_deals",
  "promo_banners_new", "new_arrivals", "featured_categories", "why_shop",
  "top_deals_week", "reseller_app", "customer_reviews",
];

const SECTION_META = {
  category_icons_row:  { label: "Category Icons Row",          icon: Grid,       color: "bg-purple-50 text-purple-600" },
  secondary_slider:    { label: "Promotional Banner Slideshow", icon: Film,       color: "bg-indigo-50 text-indigo-600" },
  shop_by_price:       { label: "Shop by Price",               icon: Zap,        color: "bg-orange-50 text-orange-500" },
  flash_deals:         { label: "Flash Deals",                 icon: Zap,        color: "bg-red-50 text-red-500" },
  promo_banners_new:   { label: "Promo Banners",               icon: ShoppingBag,color: "bg-pink-50 text-pink-500" },
  new_arrivals:        { label: "New Arrivals",                icon: Star,       color: "bg-green-50 text-green-600" },
  featured_categories: { label: "Featured Categories",         icon: Layers,     color: "bg-blue-50 text-blue-600" },
  why_shop:            { label: "Why Shop with IMPORTREND",    icon: ShoppingBag,color: "bg-teal-50 text-teal-600" },
  top_deals_week:      { label: "Top Deals This Week",         icon: Star,       color: "bg-amber-50 text-amber-600" },
  reseller_app:        { label: "Reseller + App",              icon: Smartphone, color: "bg-slate-50 text-slate-600" },
  customer_reviews:    { label: "Customer Reviews",            icon: Heart,      color: "bg-rose-50 text-rose-500" },
};

// ── Primitives ────────────────────────────────────────────────────────────
const Field = ({ label, children, help }) => (
  <div className="mb-3">
    {label && <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>}
    {children}
    {help && <p className="text-[11px] text-slate-400 mt-0.5">{help}</p>}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
);

const CategorySelect = ({ value, onChange, categories, placeholder = "All Categories" }) => (
  <select value={value || ""} onChange={e => onChange(e.target.value)}
    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
    <option value="">{placeholder}</option>
    {categories.map(c => (
      <option key={c.slug || c.name} value={c.slug || c.name}>{c.name}</option>
    ))}
  </select>
);

// ── Compact image upload field ────────────────────────────────────────────
const ImgField = ({ label, value, onChange, help }) => (
  <Field label={label} help={help}>
    <ImageUploadInput value={value} onChange={onChange} placeholder="Paste URL or click Upload" />
  </Field>
);

// ── Media upload field (image / GIF / video) ──────────────────────────────
const MediaField = ({ label, value, onChange, help }) => (
  <Field label={label} help={help}>
    <MediaUploadInput value={value} onChange={onChange} placeholder="Paste URL or click Upload" />
  </Field>
);

// ── Section wrapper with header controls ─────────────────────────────────
function SectionWrapper({ icon: Icon, title, color, children, enabled, onToggle, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-3">
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50">
        <div className={`w-7 h-7 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <h3 className="text-sm font-bold text-slate-800 flex-1">{title}</h3>
        <div className="flex items-center gap-0.5 mr-1">
          <button onClick={onMoveUp} disabled={isFirst}
            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-colors" title="Move Up">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={onMoveDown} disabled={isLast}
            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-colors" title="Move Down">
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <button type="button" onClick={() => onToggle(!enabled)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors mr-2 flex-shrink-0 ${enabled ? "bg-orange-500" : "bg-slate-300"}`}>
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4.5" : "translate-x-0.5"}`} />
        </button>
        <button onClick={() => setOpen(o => !o)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 transition-colors">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {open && <div className="p-4 border-t border-slate-100">{children}</div>}
    </div>
  );
}

// ── Options ───────────────────────────────────────────────────────────────
const GRADIENTS = [
  { label: "Pink-Fuchsia",  value: "from-pink-500 via-fuchsia-600 to-rose-500" },
  { label: "Blue-Violet",   value: "from-blue-500 via-indigo-600 to-violet-500" },
  { label: "Orange-Amber",  value: "from-orange-500 via-amber-500 to-yellow-400" },
  { label: "Dark Slate",    value: "from-slate-800 via-slate-700 to-slate-600" },
  { label: "Green-Emerald", value: "from-green-500 via-emerald-500 to-teal-400" },
  { label: "Red-Rose",      value: "from-red-500 via-rose-500 to-pink-500" },
  { label: "Purple-Indigo", value: "from-purple-600 via-violet-600 to-indigo-500" },
  { label: "Teal-Cyan",     value: "from-teal-500 via-cyan-500 to-blue-400" },
  { label: "Amber-Yellow",  value: "from-amber-500 via-yellow-500 to-orange-400" },
  { label: "Sky-Blue",      value: "from-sky-500 via-blue-500 to-indigo-400" },
];

const COLOR_OPTIONS = [
  { label: "Blue",    value: "bg-blue-100 text-blue-600" },
  { label: "Purple",  value: "bg-purple-100 text-purple-600" },
  { label: "Orange",  value: "bg-orange-100 text-orange-600" },
  { label: "Red",     value: "bg-red-100 text-red-600" },
  { label: "Pink",    value: "bg-pink-100 text-pink-600" },
  { label: "Rose",    value: "bg-rose-100 text-rose-600" },
  { label: "Yellow",  value: "bg-yellow-100 text-yellow-600" },
  { label: "Green",   value: "bg-green-100 text-green-600" },
  { label: "Teal",    value: "bg-teal-100 text-teal-600" },
  { label: "Emerald", value: "bg-emerald-100 text-emerald-600" },
  { label: "Amber",   value: "bg-amber-100 text-amber-600" },
  { label: "Slate",   value: "bg-slate-100 text-slate-600" },
];

const ICON_OPTIONS = [
  "Monitor", "Smartphone", "Home", "UtensilsCrossed", "Shirt", "Sparkles",
  "BookOpen", "Gamepad2", "Activity", "Car", "Heart", "Leaf",
  "Truck", "Package", "BadgePercent", "ShieldCheck", "RotateCcw",
  "Headphones", "Star", "Zap", "Gift", "ShoppingCart",
];

// ─────────────────────────────────────────────────────────────────────────────
// 1. CATEGORY ICONS ROW
// ─────────────────────────────────────────────────────────────────────────────
function CategoryIconsContent({ data, onChange, categories, collections }) {
  const items = data.items || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateItem = (i, f, v) => { const arr = [...items]; arr[i] = { ...arr[i], [f]: v }; update("items", arr); };
  const addItem = () => update("items", [...items, { label: "New Category", icon: "ShoppingCart", link: "/search", color: "bg-blue-100 text-blue-600", image_url: "" }]);
  const removeItem = (i) => update("items", items.filter((_, idx) => idx !== i));
  const moveItem = (i, dir) => { const arr = [...items]; [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]]; update("items", arr); };

  // Helper: populate item from a collection
  const applyCollection = (i, col) => {
    const slug = col.slug || col.name?.toLowerCase().replace(/\s+/g, "-");
    updateItem(i, "label", col.name);
    updateItem(i, "link", `/c/${slug}`);
  };

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">
        Scrollable row of category icons below the header. Each icon can link to a collection or custom URL.
        Upload a custom image, or choose a built-in icon.
      </p>

      {items.length === 0 && (
        <p className="text-xs text-slate-400 bg-slate-50 rounded-lg p-3 border border-slate-100 mb-3">
          No icons configured. Add items below or click "Load from Catalog" to auto-populate from your collections.
        </p>
      )}

      {collections.length > 0 && items.length === 0 && (
        <button
          onClick={() => {
            const autoItems = collections.slice(0, 12).map(c => ({
              label: c.name,
              icon: "ShoppingCart",
              link: `/c/${c.slug || c.name?.toLowerCase().replace(/\s+/g, "-")}`,
              color: COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)].value,
              image_url: c.image || "",
            }));
            update("items", autoItems);
          }}
          className="flex items-center gap-1 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg border border-blue-200 w-full justify-center mb-3"
        >
          Load from Catalog ({collections.length} collections)
        </button>
      )}

      <div className="space-y-3 mb-3">
        {items.map((item, i) => (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
            {/* Item header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-100">
              {item.image_url ? (
                <img src={item.image_url} alt={item.label} className="w-8 h-8 rounded-full object-cover border border-slate-200" />
              ) : (
                <div className={`w-8 h-8 rounded-full ${item.color || "bg-slate-100 text-slate-600"} flex items-center justify-center text-xs font-bold`}>
                  {item.label?.[0] || "?"}
                </div>
              )}
              <span className="flex-1 text-xs font-bold text-slate-700">{item.label || `Icon ${i + 1}`}</span>
              <div className="flex items-center gap-0.5">
                <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>

            {/* Item fields */}
            <div className="p-3 grid grid-cols-2 gap-2">
              <Field label="Label">
                <Input value={item.label || ""} onChange={e => updateItem(i, "label", e.target.value)} placeholder="Electronics" />
              </Field>
              <Field label="Quick Select from Catalog">
                <select defaultValue="" onChange={e => { if (e.target.value) { const col = collections.find(c => (c.slug || c.name) === e.target.value); if (col) applyCollection(i, col); } }}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                  <option value="" disabled>-- Apply from collection --</option>
                  {collections.map(c => <option key={c.slug || c.name} value={c.slug || c.name}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Link URL">
                <Input value={item.link || ""} onChange={e => updateItem(i, "link", e.target.value)} placeholder="/search?q=Electronics" />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Fallback Icon">
                  <select value={item.icon || "ShoppingCart"} onChange={e => updateItem(i, "icon", e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                    {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                </Field>
                <Field label="Circle Color">
                  <select value={item.color || ""} onChange={e => updateItem(i, "color", e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                    <option value="">Default</option>
                    {COLOR_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
              </div>
              {/* Show / Hide label on storefront */}
              <div className="col-span-2 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-xs font-medium text-slate-600">Show label on storefront</span>
                <button
                  type="button"
                  data-testid={`label-toggle-${i}`}
                  onClick={() => updateItem(i, "hide_label", !item.hide_label)}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${item.hide_label ? "bg-slate-300" : "bg-orange-500"}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${item.hide_label ? "left-0.5" : "left-5"}`} />
                </button>
              </div>
            </div>
            <div className="px-3 pb-3">
              <ImgField
                label="Custom Icon Image (replaces the icon)"
                value={item.image_url}
                onChange={v => updateItem(i, "image_url", v)}
                help="Upload a square image (ideal: 100×100px). This overrides the icon above."
              />
            </div>
          </div>
        ))}
      </div>

      <button onClick={addItem} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Category Icon
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. SECONDARY BANNER SLIDESHOW
// ─────────────────────────────────────────────────────────────────────────────
function SlideshowContent({ data, onChange }) {
  const slides = data.slides || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateSlide = (i, f, v) => { const arr = [...slides]; arr[i] = { ...arr[i], [f]: v }; update("slides", arr); };
  const addSlide = () => update("slides", [...slides, { title: "New Slide", subtitle: "SPECIAL OFFER", discount_text: "UP TO 50% OFF", cta_text: "Shop Now", cta_link: "/search", image_url: "", bg_gradient: "from-orange-500 via-amber-500 to-yellow-400" }]);
  const removeSlide = (i) => update("slides", slides.filter((_, idx) => idx !== i));
  const moveSlide = (i, dir) => { const arr = [...slides]; [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]]; update("slides", arr); };

  return (
    <div>
      <Field label="Autoplay Interval (ms)" help="Time between slides. 5000 = 5 seconds. Set 0 to disable autoplay.">
        <input type="number" value={data.autoplay_interval || 5000}
          onChange={e => update("autoplay_interval", Number(e.target.value))}
          min={0} max={30000} step={500}
          className="w-36 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
      </Field>

      <p className="text-xs font-semibold text-slate-700 mb-3">Slides ({slides.length})</p>

      {slides.length === 0 && (
        <p className="text-xs text-slate-400 bg-slate-50 rounded-lg p-3 border border-slate-100 mb-3">No slides yet. Add a slide below.</p>
      )}

      <div className="space-y-4 mb-3">
        {slides.map((sl, i) => (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-6 rounded bg-gradient-to-r ${sl.bg_gradient || "from-gray-300 to-gray-400"}`} />
                <span className="text-xs font-bold text-slate-700">Slide {i + 1}: {sl.title || "(untitled)"}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <button onClick={() => moveSlide(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => moveSlide(i, 1)} disabled={i === slides.length - 1} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => removeSlide(i)} className="p-1 text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="p-3">
              <ImgField
                label="Background Image (banner)"
                value={sl.image_url}
                onChange={v => updateSlide(i, "image_url", v)}
                help="Upload a wide banner image (recommended: 1600×400px). Overrides the gradient."
              />
              <Field label="Background Gradient (used when no image is set)">
                <div className="flex gap-2">
                  {sl.bg_gradient && <div className={`w-10 h-8 rounded-lg bg-gradient-to-r ${sl.bg_gradient} flex-shrink-0`} />}
                  <select value={sl.bg_gradient || ""} onChange={e => updateSlide(i, "bg_gradient", e.target.value)}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                    <option value="">-- Select Gradient --</option>
                    {GRADIENTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Headline"><Input value={sl.title || ""} onChange={e => updateSlide(i, "title", e.target.value)} placeholder="Amazing Deals" /></Field>
                <Field label="Eyebrow Text (small)"><Input value={sl.subtitle || ""} onChange={e => updateSlide(i, "subtitle", e.target.value)} placeholder="SPECIAL OFFER" /></Field>
                <Field label="Discount Badge"><Input value={sl.discount_text || ""} onChange={e => updateSlide(i, "discount_text", e.target.value)} placeholder="UP TO 50% OFF" /></Field>
                <Field label="CTA Button Text"><Input value={sl.cta_text || ""} onChange={e => updateSlide(i, "cta_text", e.target.value)} placeholder="Shop Now" /></Field>
                <Field label="CTA Link"><Input value={sl.cta_link || ""} onChange={e => updateSlide(i, "cta_link", e.target.value)} placeholder="/search" /></Field>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={addSlide} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Slide
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. SHOP BY PRICE
// ─────────────────────────────────────────────────────────────────────────────
function ShopByPriceContent({ data, onChange, categories }) {
  const buckets = data.buckets || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateBucket = (i, f, v) => { const arr = [...buckets]; arr[i] = { ...arr[i], [f]: v }; update("buckets", arr); };
  const addBucket = () => update("buckets", [...buckets, { label: "Under ₹499", sub: "Top Selections", max: 499, gradient: "from-rose-500 to-pink-400", image_url: "" }]);
  const removeBucket = (i) => update("buckets", buckets.filter((_, idx) => idx !== i));
  const moveBucket = (i, dir) => {
    const arr = [...buckets];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update("buckets", arr);
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Section Title"><Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="Shop by Price" /></Field>
        <Field label="Subtitle"><Input value={data.subtitle || ""} onChange={e => update("subtitle", e.target.value)} placeholder="Deals at every budget" /></Field>
        <Field label="Section Tag (e.g. NEW, TRENDING)" help="Shown as a badge next to the section title"><Input value={data.tag || ""} onChange={e => update("tag", e.target.value)} placeholder="NEW" /></Field>
        <Field label="Show Section Title on Storefront">
          <label className="flex items-center gap-2 cursor-pointer mt-1">
            <input type="checkbox" checked={data.show_title !== false} onChange={e => update("show_title", e.target.checked)} className="w-4 h-4 accent-orange-500 cursor-pointer" />
            <span className="text-sm text-slate-700">{data.show_title !== false ? "Visible" : "Hidden"}</span>
          </label>
        </Field>
        <Field label="Show Subtitle on Storefront">
          <label className="flex items-center gap-2 cursor-pointer mt-1">
            <input type="checkbox" checked={data.show_subtitle !== false} onChange={e => update("show_subtitle", e.target.checked)} className="w-4 h-4 accent-orange-500 cursor-pointer" />
            <span className="text-sm text-slate-700">{data.show_subtitle !== false ? "Visible" : "Hidden"}</span>
          </label>
        </Field>
        <Field label="Rows to Show" help="Each row displays 5 banners. Extras appear in the 'View All' modal.">
          <input
            type="number"
            value={data.rows || 1}
            onChange={e => update("rows", Math.max(1, Number(e.target.value)))}
            min={1} max={10}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white"
          />
        </Field>
      </div>

      <p className="text-xs font-semibold text-slate-700 mb-2">Price Banners ({buckets.length}) — 5 per row</p>
      <p className="text-xs text-slate-400 bg-blue-50 border border-blue-100 rounded-lg p-2.5 mb-3">
        Upload an image, GIF, or video (max 50 MB) for each banner. Use the arrows to reorder banners.
      </p>
      <div className="space-y-3 mb-3">
        {buckets.map((b, i) => (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                {/* Preview thumbnail — image/gif or gradient swatch */}
                {b.image_url && !/\.(mp4|webm|mov|ogv)(\?.*)?$/i.test(b.image_url) ? (
                  <img
                    src={b.image_url}
                    alt={b.label || `Banner ${i + 1}`}
                    className="w-14 h-9 rounded object-cover border border-slate-200 flex-shrink-0"
                  />
                ) : b.image_url ? (
                  <div className="w-14 h-9 rounded bg-slate-800 flex items-center justify-center flex-shrink-0 border border-slate-200">
                    <Film className="w-4 h-4 text-slate-300" />
                  </div>
                ) : (
                  <div className={`w-14 h-9 rounded bg-gradient-to-r ${b.gradient || "from-gray-300 to-gray-400"} flex-shrink-0`} />
                )}
                <span className="text-xs font-bold text-slate-700">{b.label || `Banner ${i + 1}`}</span>
                <span className="text-[10px] text-slate-400 font-mono">#{i + 1}</span>
              </div>
              <div className="flex items-center gap-1">
                {/* Reorder arrows */}
                <button
                  type="button"
                  onClick={() => moveBucket(i, -1)}
                  disabled={i === 0}
                  className="w-6 h-6 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-orange-600 hover:border-orange-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move up"
                ><ChevronUp className="w-3 h-3" /></button>
                <button
                  type="button"
                  onClick={() => moveBucket(i, 1)}
                  disabled={i === buckets.length - 1}
                  className="w-6 h-6 flex items-center justify-center rounded border border-slate-200 text-slate-400 hover:text-orange-600 hover:border-orange-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move down"
                ><ChevronDown className="w-3 h-3" /></button>
                <button onClick={() => removeBucket(i)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="p-3">
              <MediaField
                label="Banner Media (image / GIF / video)"
                value={b.image_url}
                onChange={v => updateBucket(i, "image_url", v)}
                help="Fills the entire banner card as a full-cover background. Leave empty to use the colour gradient."
              />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Field label="Label"><Input value={b.label || ""} onChange={e => updateBucket(i, "label", e.target.value)} placeholder="Under ₹199" /></Field>
                <Field label="Sub Text"><Input value={b.sub || ""} onChange={e => updateBucket(i, "sub", e.target.value)} placeholder="Budget Buys" /></Field>
                <Field label="Max Price (₹)" help="Used as fallback redirect if no URL is set below">
                  <input type="number" value={b.max || ""} onChange={e => updateBucket(i, "max", Number(e.target.value))} min={1}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
                </Field>
                <Field label="Fallback Gradient (no media)">
                  <div className="flex gap-2 items-center">
                    {b.gradient && <div className={`w-8 h-7 rounded bg-gradient-to-r ${b.gradient} flex-shrink-0`} />}
                    <select value={b.gradient || ""} onChange={e => updateBucket(i, "gradient", e.target.value)}
                      className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                      <option value="">-- Select --</option>
                      {GRADIENTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                </Field>
              </div>

              {/* Tag / enable / device */}
              <div className="grid grid-cols-3 gap-2 mt-2 items-end">
                <Field label="Tag (e.g. NEW)" help="Badge at the top-left of the banner"><Input value={b.tag || ""} onChange={e => updateBucket(i, "tag", e.target.value)} placeholder="TRENDING" /></Field>
                <Field label="Show banner on">
                  <select value={b.device || "both"} onChange={e => updateBucket(i, "device", e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
                    <option value="both">Mobile + Desktop</option>
                    <option value="mobile">Mobile only</option>
                    <option value="desktop">Desktop only</option>
                  </select>
                </Field>
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <input type="checkbox" checked={b.enabled !== false} onChange={e => updateBucket(i, "enabled", e.target.checked)} className="w-4 h-4 accent-orange-500" />
                  <span className="text-sm text-slate-700">Enabled</span>
                </label>
              </div>

              {/* Redirect URL */}
              <Field label="Redirect URL" help="Where the user lands when clicking this banner. Leave empty to use Max Price filter." className="mt-2">
                <Input
                  value={b.link_url || ""}
                  onChange={e => updateBucket(i, "link_url", e.target.value)}
                  placeholder="/collections/jewellery  or  /search?q=comb  or  /product/abc123"
                />
              </Field>

              {/* Text & button toggles */}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Field label="Show Label & Sub Text">
                  <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={b.show_text !== false}
                      onChange={e => updateBucket(i, "show_text", e.target.checked)}
                      className="w-4 h-4 accent-orange-500 cursor-pointer"
                    />
                    <span className="text-sm text-slate-700">{b.show_text !== false ? "Visible" : "Hidden"}</span>
                  </label>
                </Field>
                <Field label="'Shop Now' Button">
                  <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={b.show_shop_now !== false}
                      onChange={e => updateBucket(i, "show_shop_now", e.target.checked)}
                      className="w-4 h-4 accent-orange-500 cursor-pointer"
                    />
                    <span className="text-sm text-slate-700">{b.show_shop_now !== false ? "Visible" : "Hidden"}</span>
                  </label>
                </Field>
                <Field label="Button Background Color" help="Leave empty for default semi-transparent">
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={b.shop_now_color || "#ffffff"}
                      onChange={e => updateBucket(i, "shop_now_color", e.target.value)}
                      className="w-10 h-9 rounded border border-slate-200 cursor-pointer p-0.5 flex-shrink-0"
                    />
                    <Input
                      value={b.shop_now_color || ""}
                      onChange={e => updateBucket(i, "shop_now_color", e.target.value)}
                      placeholder="#ff5500"
                    />
                    {b.shop_now_color && (
                      <button onClick={() => updateBucket(i, "shop_now_color", "")} className="text-slate-400 hover:text-red-500 flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </Field>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addBucket} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Price Banner
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. FLASH DEALS
// ─────────────────────────────────────────────────────────────────────────────
function FlashDealsContent({ data, onChange, categories }) {
  const update = (f, v) => onChange({ ...data, [f]: v });
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Section Title"><Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="Today's Flash Deals" /></Field>
        <Field label="Countdown Hours" help="Countdown timer resets to this value on each page load">
          <input type="number" value={data.countdown_hours || 12} onChange={e => update("countdown_hours", Number(e.target.value))} min={1} max={48}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
        </Field>
        <Field label="Max Products to Show">
          <input type="number" value={data.max_products || 8} onChange={e => update("max_products", Number(e.target.value))} min={2} max={20}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
        </Field>
        <Field label="Filter by Category" help="Show deals only from this category (leave blank for all)">
          <CategorySelect value={data.filter_category} onChange={v => update("filter_category", v)} categories={categories} />
        </Field>
      </div>
      <p className="text-xs text-slate-400 bg-amber-50 border border-amber-100 rounded-lg p-2.5">
        Products auto-sorted by highest discount. Only products with MRP &gt; sale price are displayed.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. PROMO BANNERS
// ─────────────────────────────────────────────────────────────────────────────
function PromoBannersContent({ data, onChange }) {
  const banners = data.banners || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateBanner = (i, f, v) => { const arr = [...banners]; arr[i] = { ...arr[i], [f]: v }; update("banners", arr); };
  const addBanner = () => update("banners", [...banners, { title: "New Banner", subtitle: "Promotion", discount: "UP TO 50% OFF", link: "/search", gradient: "from-orange-500 via-amber-500 to-yellow-400", cta_text: "Shop Now", image_url: "" }]);
  const removeBanner = (i) => update("banners", banners.filter((_, idx) => idx !== i));

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">Side-by-side promotional banners displayed in a 2-column grid.</p>
      <div className="space-y-4 mb-3">
        {banners.map((b, i) => (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className={`px-3 py-1 rounded text-xs font-bold text-white bg-gradient-to-r ${b.gradient || "from-gray-400 to-gray-500"}`}>Banner {i + 1}</div>
              </div>
              <button onClick={() => removeBanner(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-3">
              <ImgField
                label="Banner Image (optional)"
                value={b.image_url}
                onChange={v => updateBanner(i, "image_url", v)}
                help="Upload a banner image. Displays as product image overlay on the right side of the banner."
              />
              <Field label="Background Gradient">
                <div className="flex gap-2 items-center">
                  {b.gradient && <div className={`w-8 h-7 rounded bg-gradient-to-r ${b.gradient} flex-shrink-0`} />}
                  <select value={b.gradient || ""} onChange={e => updateBanner(i, "gradient", e.target.value)}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                    <option value="">-- Select Gradient --</option>
                    {GRADIENTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Title"><Input value={b.title || ""} onChange={e => updateBanner(i, "title", e.target.value)} /></Field>
                <Field label="Subtitle / Eyebrow"><Input value={b.subtitle || ""} onChange={e => updateBanner(i, "subtitle", e.target.value)} /></Field>
                <Field label="Discount Text"><Input value={b.discount || ""} onChange={e => updateBanner(i, "discount", e.target.value)} placeholder="UP TO 60% OFF" /></Field>
                <Field label="CTA Text"><Input value={b.cta_text || ""} onChange={e => updateBanner(i, "cta_text", e.target.value)} placeholder="Shop Now" /></Field>
                <Field label="CTA Link" className="col-span-2"><Input value={b.link || ""} onChange={e => updateBanner(i, "link", e.target.value)} placeholder="/search" /></Field>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addBanner} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Banner
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. NEW ARRIVALS
// ─────────────────────────────────────────────────────────────────────────────
function NewArrivalsContent({ data, onChange, categories }) {
  const update = (f, v) => onChange({ ...data, [f]: v });
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Field label="Section Title"><Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="New Arrivals" /></Field>
        <Field label="Subtitle"><Input value={data.subtitle || ""} onChange={e => update("subtitle", e.target.value)} placeholder="Fresh Picks for You" /></Field>
        <Field label="Max Products to Show">
          <input type="number" value={data.max_products || 10} onChange={e => update("max_products", Number(e.target.value))} min={2} max={20}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
        </Field>
        <Field label="Filter by Category" help="Show new arrivals from this category only">
          <CategorySelect value={data.filter_category} onChange={v => update("filter_category", v)} categories={categories} />
        </Field>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. FEATURED CATEGORIES
// ─────────────────────────────────────────────────────────────────────────────
function FeaturedCategoriesContent({ data, onChange, collections }) {
  const update = (f, v) => onChange({ ...data, [f]: v });
  const selectedSlugs = data.selected_slugs || [];

  const addSlug = (slug) => {
    if (!slug || selectedSlugs.includes(slug)) return;
    update("selected_slugs", [...selectedSlugs, slug]);
  };
  const removeSlug = (slug) => update("selected_slugs", selectedSlugs.filter(s => s !== slug));
  const moveSlug = (i, dir) => { const arr = [...selectedSlugs]; [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]]; update("selected_slugs", arr); };
  const availableCollections = collections.filter(c => !selectedSlugs.includes(c.slug || c.name?.toLowerCase().replace(/\s+/g, "-")));

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Section Title"><Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="Featured Categories" /></Field>
        <Field label="Subtitle"><Input value={data.subtitle || ""} onChange={e => update("subtitle", e.target.value)} placeholder="Explore our top categories" /></Field>
      </div>
      <Field label="Selected Categories (in display order)" help="When empty, auto-shows the first N categories from your catalog">
        {selectedSlugs.length > 0 ? (
          <div className="space-y-1 mb-2">
            {selectedSlugs.map((slug, i) => {
              const col = collections.find(c => (c.slug || c.name?.toLowerCase().replace(/\s+/g, "-")) === slug);
              return (
                <div key={slug} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-1.5 border border-blue-100">
                  {col?.image && <img src={col.image} alt={col.name} className="w-6 h-6 rounded object-cover" />}
                  <span className="flex-1 text-xs font-semibold text-blue-800">{col?.name || slug}</span>
                  <button onClick={() => moveSlug(i, -1)} disabled={i === 0} className="p-0.5 text-blue-400 hover:text-blue-600 disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                  <button onClick={() => moveSlug(i, 1)} disabled={i === selectedSlugs.length - 1} className="p-0.5 text-blue-400 hover:text-blue-600 disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                  <button onClick={() => removeSlug(slug)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-slate-400 mb-2">No specific categories selected — showing first {data.max_categories || 6} automatically.</p>
        )}
        {availableCollections.length > 0 && (
          <select defaultValue="" onChange={e => { addSlug(e.target.value); e.target.value = ""; }}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
            <option value="" disabled>+ Add a category...</option>
            {availableCollections.map(c => {
              const slug = c.slug || c.name?.toLowerCase().replace(/\s+/g, "-");
              return <option key={slug} value={slug}>{c.name}</option>;
            })}
          </select>
        )}
      </Field>
      {selectedSlugs.length === 0 && (
        <Field label="Max Categories (auto mode)">
          <input type="number" value={data.max_categories || 6} onChange={e => update("max_categories", Number(e.target.value))} min={2} max={12}
            className="w-24 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
        </Field>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. WHY SHOP
// ─────────────────────────────────────────────────────────────────────────────
function WhyShopContent({ data, onChange }) {
  const items = data.items || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateItem = (i, f, v) => { const arr = [...items]; arr[i] = { ...arr[i], [f]: v }; update("items", arr); };
  const addItem = () => update("items", [...items, { icon: "Star", label: "New Benefit", sub: "Short description" }]);
  const removeItem = (i) => update("items", items.filter((_, idx) => idx !== i));

  return (
    <div>
      <Field label="Section Title">
        <Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="Why Shop with IMPORTREND" />
      </Field>
      <p className="text-xs font-semibold text-slate-700 mb-2 mt-1">Benefit Items</p>
      <div className="space-y-2 mb-3">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5 border border-slate-100">
            <select value={item.icon || "Star"} onChange={e => updateItem(i, "icon", e.target.value)}
              className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none w-32">
              {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
            </select>
            <Input value={item.label || ""} onChange={e => updateItem(i, "label", e.target.value)} placeholder="Benefit title" />
            <Input value={item.sub || ""} onChange={e => updateItem(i, "sub", e.target.value)} placeholder="Short description" />
            <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button onClick={addItem} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Benefit
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. TOP DEALS THIS WEEK
// ─────────────────────────────────────────────────────────────────────────────
function TopDealsContent({ data, onChange, categories }) {
  const tiles = data.tiles || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateTile = (i, f, v) => { const arr = [...tiles]; arr[i] = { ...arr[i], [f]: v }; update("tiles", arr); };
  const addTile = () => update("tiles", [...tiles, { label: "New Category", price: "Under ₹299", color: "from-teal-500 via-cyan-500 to-blue-400", q: "", image_url: "" }]);
  const removeTile = (i) => update("tiles", tiles.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Section Title"><Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="Top Deals This Week" /></Field>
        <Field label="Subtitle"><Input value={data.subtitle || ""} onChange={e => update("subtitle", e.target.value)} placeholder="Limited time offers" /></Field>
      </div>
      <Field label="Global Category Filter" help="When set, applies this category across all tile searches">
        <CategorySelect value={data.filter_category} onChange={v => update("filter_category", v)} categories={categories} />
      </Field>

      <p className="text-xs font-semibold text-slate-700 mb-2">Deal Tiles ({tiles.length})</p>
      <div className="space-y-3 mb-3">
        {tiles.map((t, i) => (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-6 rounded bg-gradient-to-r ${t.color || "from-gray-300 to-gray-400"}`} />
                <span className="text-xs font-bold text-slate-700">{t.label || `Tile ${i + 1}`}</span>
              </div>
              <button onClick={() => removeTile(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-3">
              <ImgField
                label="Tile Background Image (optional)"
                value={t.image_url}
                onChange={v => updateTile(i, "image_url", v)}
                help="Upload an image for this tile's background."
              />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Tile Label"><Input value={t.label || ""} onChange={e => updateTile(i, "label", e.target.value)} placeholder="Smart Gadgets" /></Field>
                <Field label="Price Label"><Input value={t.price || ""} onChange={e => updateTile(i, "price", e.target.value)} placeholder="Under ₹199" /></Field>
                <Field label="Link to Category">
                  <CategorySelect value={t.q} onChange={v => updateTile(i, "q", v)} categories={categories} placeholder="All Products" />
                </Field>
                <Field label="Gradient Color">
                  <div className="flex gap-2 items-center">
                    {t.color && <div className={`w-8 h-7 rounded bg-gradient-to-r ${t.color} flex-shrink-0`} />}
                    <select value={t.color || ""} onChange={e => updateTile(i, "color", e.target.value)}
                      className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                      <option value="">-- Select --</option>
                      {GRADIENTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                </Field>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addTile} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Tile
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. RESELLER + APP
// ─────────────────────────────────────────────────────────────────────────────
function ResellerAppContent({ data, onChange }) {
  const reseller = data.reseller || {};
  const app = data.app || {};
  const updateReseller = (f, v) => onChange({ ...data, reseller: { ...reseller, [f]: v } });
  const updateApp = (f, v) => onChange({ ...data, app: { ...app, [f]: v } });
  const features = reseller.features || [];
  const updateFeature = (i, v) => { const arr = [...features]; arr[i] = v; updateReseller("features", arr); };
  const addFeature = () => updateReseller("features", [...features, "New Feature"]);
  const removeFeature = (i) => updateReseller("features", features.filter((_, idx) => idx !== i));

  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <p className="text-xs font-bold text-slate-700 mb-3">Reseller Section</p>
        <Field label="Title"><Input value={reseller.title || ""} onChange={e => updateReseller("title", e.target.value)} placeholder="Become a Reseller" /></Field>
        <Field label="Subtitle"><Input value={reseller.subtitle || ""} onChange={e => updateReseller("subtitle", e.target.value)} placeholder="Start your online business..." /></Field>
        <Field label="CTA Text"><Input value={reseller.cta_text || ""} onChange={e => updateReseller("cta_text", e.target.value)} placeholder="Register Now" /></Field>
        <Field label="CTA Link"><Input value={reseller.cta_link || ""} onChange={e => updateReseller("cta_link", e.target.value)} placeholder="/pages/become-a-reseller" /></Field>
        <p className="text-xs font-semibold text-slate-700 mb-1 mt-2">Benefits Checklist</p>
        {features.map((f, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5">
            <Input value={f} onChange={e => updateFeature(i, e.target.value)} />
            <button onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={addFeature} className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200 border-dashed mt-1">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div>
        <p className="text-xs font-bold text-slate-700 mb-3">App Download Section</p>
        <Field label="Title"><Input value={app.title || ""} onChange={e => updateApp("title", e.target.value)} placeholder="Download Our App" /></Field>
        <Field label="Subtitle"><Input value={app.subtitle || ""} onChange={e => updateApp("subtitle", e.target.value)} placeholder="Shop anytime, anywhere" /></Field>
        <ImgField label="App Screenshot / Promo Image" value={app.image_url} onChange={v => updateApp("image_url", v)} help="Optional app screenshot or promo image" />
        <p className="text-[11px] text-slate-400 mt-2">Play Store / App Store buttons link to "#". Update when your app is live.</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 11. CUSTOMER REVIEWS
// ─────────────────────────────────────────────────────────────────────────────
function CustomerReviewsContent({ data, onChange }) {
  const reviews = data.reviews || [];
  const update = (f, v) => onChange({ ...data, [f]: v });
  const updateReview = (i, f, v) => { const arr = [...reviews]; arr[i] = { ...arr[i], [f]: v }; update("reviews", arr); };
  const addReview = () => update("reviews", [...reviews, { name: "Customer Name", location: "City", text: "Great products!", rating: 5 }]);
  const removeReview = (i) => update("reviews", reviews.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Section Title"><Input value={data.title || ""} onChange={e => update("title", e.target.value)} placeholder="What Our Customers Say" /></Field>
        <Field label="Subtitle"><Input value={data.subtitle || ""} onChange={e => update("subtitle", e.target.value)} placeholder="Real reviews from happy shoppers" /></Field>
      </div>
      <div className="space-y-3 mb-3">
        {reviews.map((r, i) => (
          <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
              <span className="text-xs font-bold text-slate-700">{r.name || `Review ${i + 1}`}</span>
              <button onClick={() => removeReview(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-3">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Customer Name"><Input value={r.name || ""} onChange={e => updateReview(i, "name", e.target.value)} /></Field>
                <Field label="Location / City"><Input value={r.location || ""} onChange={e => updateReview(i, "location", e.target.value)} /></Field>
              </div>
              <Field label="Review Text">
                <textarea value={r.text || ""} onChange={e => updateReview(i, "text", e.target.value)} rows={2}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none" />
              </Field>
              <Field label="Rating">
                <select value={r.rating || 5} onChange={e => updateReview(i, "rating", Number(e.target.value))}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white w-28">
                  {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{"★".repeat(n)} {n}/5</option>)}
                </select>
              </Field>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addReview} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
        <Plus className="w-3.5 h-3.5" /> Add Review
      </button>
    </div>
  );
}

// ── Render content by section key ─────────────────────────────────────────
function renderContent(key, config, onChange, { categories, collections }) {
  const data = config[key] || {};
  const upd = v => onChange({ ...config, [key]: v });
  switch (key) {
    case "category_icons_row":  return <CategoryIconsContent data={data} onChange={upd} categories={categories} collections={collections} />;
    case "secondary_slider":    return <SlideshowContent data={data} onChange={upd} />;
    case "shop_by_price":       return <ShopByPriceContent data={data} onChange={upd} categories={categories} />;
    case "flash_deals":         return <FlashDealsContent data={data} onChange={upd} categories={categories} />;
    case "promo_banners_new":   return <PromoBannersContent data={data} onChange={upd} />;
    case "new_arrivals":        return <NewArrivalsContent data={data} onChange={upd} categories={categories} />;
    case "featured_categories": return <FeaturedCategoriesContent data={data} onChange={upd} collections={collections} />;
    case "why_shop":            return <WhyShopContent data={data} onChange={upd} />;
    case "top_deals_week":      return <TopDealsContent data={data} onChange={upd} categories={categories} />;
    case "reseller_app":        return <ResellerAppContent data={data} onChange={upd} />;
    case "customer_reviews":    return <CustomerReviewsContent data={data} onChange={upd} />;
    default: return null;
  }
}

// Named export for use in HomepageBuilder unified sections view
export function NewSectionContentRenderer({ sectionKey, config, onChange, categories, collections }) {
  return renderContent(sectionKey, config, onChange, { categories, collections });
}

// ── Main export ───────────────────────────────────────────────────────────
export default function NewSectionsEditor({ config = {}, onChange }) {
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("it_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/admin/categories`, { headers })
      .then(r => setCategories(r.data || []))
      .catch(() => {});
    axios.get(`${API}/collections/public`)
      .then(r => {
        const raw = r.data || [];
        // Normalize: API returns either plain strings or objects
        const normalized = raw.map(c =>
          typeof c === "string"
            ? { name: c, slug: c.toLowerCase().replace(/\s+/g, "-") }
            : c
        );
        setCollections(normalized);
      })
      .catch(() => {});
  }, []);

  const order = config.new_sections_order?.length ? config.new_sections_order : DEFAULT_ORDER;

  const moveSection = (idx, dir) => {
    const arr = [...order];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    onChange({ ...config, new_sections_order: arr });
  };

  const toggleSection = (key, enabled) => {
    const data = config[key] || {};
    onChange({ ...config, [key]: { ...data, enabled } });
  };

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-xs text-blue-700">
        <strong>New Homepage Sections</strong> — Use <strong>↑↓ arrows</strong> to reorder, the toggle to show/hide,
        and click <strong>▼</strong> to expand and edit each section.
        Press <strong>Save Changes</strong> at the top of the page when done.
      </div>

      {order.map((key, idx) => {
        const meta = SECTION_META[key];
        if (!meta) return null;
        const sectionData = config[key] || {};
        const enabled = sectionData.enabled !== false;

        return (
          <SectionWrapper
            key={key}
            icon={meta.icon}
            title={meta.label}
            color={meta.color}
            enabled={enabled}
            onToggle={v => toggleSection(key, v)}
            onMoveUp={() => moveSection(idx, -1)}
            onMoveDown={() => moveSection(idx, 1)}
            isFirst={idx === 0}
            isLast={idx === order.length - 1}
          >
            {renderContent(key, config, onChange, { categories, collections })}
          </SectionWrapper>
        );
      })}
    </div>
  );
}
